import { useCallback, useEffect, useState } from 'react';
import api from '../lib/api';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { ArrowRight, Link2, Loader2, Trash2 } from 'lucide-react';

const NamePicker = ({ value, onChange, names, placeholder, testid, exclude }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="rounded-xl" data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent className="max-h-72">
      {names.filter((n) => n.name !== exclude).map((n) => (
        <SelectItem key={n.name} value={n.name} data-testid={`${testid}-option-${n.name}`}>
          {n.name} <span className="text-[11px] text-[#685B72]/70">· {n.visits} visits · last {n.last_visit || '—'}</span>
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

const AliasRow = ({ a, onRemove }) => (
  <div className="flex items-center justify-between gap-3 py-2.5 border-b border-[#EFEAF2] last:border-0" data-testid={`alias-row-${a.id}`}>
    <div className="flex items-center gap-2 text-sm text-[#2D2833] flex-wrap">
      <span className="line-through text-[#685B72]/80">{a.old_name}</span>
      <ArrowRight size={14} className="text-[#685B72]" />
      <span className="font-semibold">{a.new_name}</span>
      <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${a.merge_status === 'failed' ? 'bg-[#FBEDED] text-[#8A3A3A]' : a.merge_status === 'merging' ? 'bg-[#FFF6E5] text-[#8A6A1E]' : 'bg-[#EEF4F2] text-[#3F7D6E]'}`}
        data-testid={`alias-status-${a.id}`}>
        {a.merge_status === 'merging' ? 'merging…' : a.merge_status}
      </span>
    </div>
    <Button variant="ghost" size="sm" onClick={() => onRemove(a)} className="text-[#8A3A3A]" data-testid={`alias-remove-${a.id}`}>
      <Trash2 size={14} />
    </Button>
  </div>
);

export const ProviderAliasPanel = () => {
  const [aliases, setAliases] = useState([]);
  const [names, setNames] = useState([]);
  const [oldName, setOldName] = useState('');
  const [newName, setNewName] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [a, n] = await Promise.all([api.get('/admin/provider-aliases'), api.get('/admin/provider-names')]);
      setAliases(a.data.aliases || []);
      setNames(n.data.names || []);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not load name links'); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!aliases.some((a) => a.merge_status === 'merging')) return undefined;
    const t = setTimeout(load, 3000);
    return () => clearTimeout(t);
  }, [aliases, load]);

  const link = async () => {
    setSaving(true);
    try {
      await api.post('/admin/provider-aliases', { old_name: oldName, new_name: newName });
      toast.success(`Linked ${oldName} → ${newName}. Merging past data and refreshing dashboards…`);
      setOldName(''); setNewName('');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not link names');
    } finally { setSaving(false); }
  };
  const remove = async (a) => {
    if (!window.confirm(`Stop merging "${a.old_name}" into "${a.new_name}" on future uploads? Already-merged data stays merged.`)) return;
    try {
      await api.delete(`/admin/provider-aliases/${encodeURIComponent(a.id)}`);
      toast.success('Link removed');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not remove link'); }
  };

  return (
    <Card className="rounded-2xl border-[#E8E5EC] p-6" data-testid="provider-alias-panel">
      <h3 className="font-serif text-lg text-[#2D2833] flex items-center gap-2"><Link2 size={16} /> Clinician Name Changes</h3>
      <p className="text-[12px] text-[#685B72] mt-1 mb-4">
        When a clinician's name changes in SimplePractice, link the old name to the new one. Past visits are merged right away (duplicates removed and backed up), every future upload is renamed automatically, and dashboards refresh.
      </p>
      <div className="grid md:grid-cols-[1fr_auto_1fr_auto] gap-2 items-center mb-5">
        <NamePicker value={oldName} onChange={setOldName} names={names} exclude={newName} placeholder="Old name…" testid="alias-old-name" />
        <ArrowRight size={16} className="text-[#685B72] justify-self-center" />
        <NamePicker value={newName} onChange={setNewName} names={names} exclude={oldName} placeholder="Current name…" testid="alias-new-name" />
        <Button onClick={link} disabled={!oldName || !newName || saving} className="bg-[#685B72] hover:bg-[#52475A] text-white rounded-xl" data-testid="alias-link-btn">
          {saving ? <Loader2 size={14} className="animate-spin" /> : 'Link names'}
        </Button>
      </div>
      <div data-testid="alias-list">
        {aliases.length === 0
          ? <div className="text-sm text-[#685B72]/80" data-testid="alias-empty">No linked names yet.</div>
          : aliases.map((a) => <AliasRow key={a.id} a={a} onRemove={remove} />)}
      </div>
    </Card>
  );
};

export default ProviderAliasPanel;
